import { create } from 'zustand';
import { CSSProperties, useId, useMemo } from 'react';
import axios from '../axios-base';

export type BackgroundType = 'keyVisual' | 'solid';
export type HorizontalPosition = 'left' | 'center' | 'right';
export type VerticalPosition = 'top' | 'center' | 'bottom';
export type CSSValueWithUnit = string | number;

export const formatCSSValue = (val: CSSValueWithUnit | undefined | null): string | undefined => {
    if (val === undefined || val === null) return undefined;
    if (typeof val === 'number') return `${val}px`;
    const str = String(val).trim();
    if (!str) return undefined;
    if (/^[0-9.-]+(px|em|rem|cqh|cqw|%)$/.test(str)) return str;
    if (!isNaN(parseFloat(str)) && isFinite(Number(str))) return `${str}px`;
    return str;
};

export interface TextStyleConfig {
    fontSize?: string | { [breakpoint: string]: string };
    color?: string;
    backgroundColor?: string;
    fontWeight?: string;
    fontStyle?: string;
    lineHeight?: string;
    textShadow?: string;
    fontFamily?: string;
    textTransform?: string;
    letterSpacing?: string;
}

export const getInlineStyles = (styleConfig?: TextStyleConfig): CSSProperties => {
    if (!styleConfig) return {};
    const styles: CSSProperties = {};
    if (styleConfig.fontSize && typeof styleConfig.fontSize === 'string') {
        styles.fontSize = styleConfig.fontSize;
    }
    if (styleConfig.color) styles.color = styleConfig.color;
    if (styleConfig.backgroundColor) styles.backgroundColor = styleConfig.backgroundColor;
    if (styleConfig.fontWeight) styles.fontWeight = styleConfig.fontWeight as any;
    if (styleConfig.fontStyle) styles.fontStyle = styleConfig.fontStyle;
    if (styleConfig.lineHeight) styles.lineHeight = styleConfig.lineHeight;
    if (styleConfig.textShadow) styles.textShadow = styleConfig.textShadow;
    if (styleConfig.fontFamily) styles.fontFamily = styleConfig.fontFamily;
    if (styleConfig.textTransform) styles.textTransform = styleConfig.textTransform as any;
    if (styleConfig.letterSpacing) styles.letterSpacing = styleConfig.letterSpacing;
    return styles;
};

export const useResponsiveFontSize = (fontSize?: string | { [breakpoint: string]: string }) => {
    const reactId = useId();
    const uniqueClass = useMemo(() => {
        return 'fs-' + reactId.replace(/:/g, '');
    }, [reactId]);

    return useMemo(() => {
        if (!fontSize) {
            return { fontSizeStyle: {}, className: '', styleText: '' };
        }

        if (typeof fontSize === 'string') {
            return { fontSizeStyle: { fontSize }, className: '', styleText: '' };
        }

        // If it's an object/map of breakpoints
        const keys = Object.keys(fontSize);
        if (keys.length === 0) {
            return { fontSizeStyle: {}, className: '', styleText: '' };
        }

        const bpMap: { [key: string]: number } = {
            xs: 0,
            sm: 576,
            md: 768,
            lg: 992,
            xl: 1200,
            xxl: 1400,
        };

        const styleText = Object.entries(fontSize)
            .map(([bp, size]) => {
                if (!size) return '';
                const minWidth = bpMap[bp.toLowerCase()] !== undefined ? bpMap[bp.toLowerCase()] : Number(bp);
                if (isNaN(minWidth)) return '';
                return `@media (min-width: ${minWidth}px) {
                    .${uniqueClass} {
                        font-size: ${formatCSSValue(size)} !important;
                    }
                }`;
            })
            .filter(Boolean)
            .join('\n');

        return {
            fontSizeStyle: {},
            className: uniqueClass,
            styleText,
        };
    }, [fontSize, uniqueClass]);
};

export interface HeroTitleConfig {
    hasTitle: boolean;
    hasSubTitle: boolean;
    horizontalPosition: HorizontalPosition;
    verticalPosition: VerticalPosition;
    text?: string;
    subtitleText?: string;
    textStyle?: TextStyleConfig;
    subtitleStyle?: TextStyleConfig;
}

export interface HeroCTAConfig {
    hasCTA: boolean;
    horizontalPosition: HorizontalPosition;
    verticalPosition: VerticalPosition;
    title?: {
        text?: string;
        style?: TextStyleConfig;
    };
    body?: {
        text?: string;
        style?: TextStyleConfig;
    };
    button?: {
        text?: string;
        color?: string;
        linkUrl?: string;
        isOutlined: boolean;
        isVisible: boolean;
        style?: TextStyleConfig;
    };
    card?: {
        backgroundColor?: string;
        border?: string;
        backdropFilter?: string;
        boxShadow?: string;
        hasNoCard: boolean;
    };
}

export interface LeadGraphicConfig {
    hasLeadGraphic: boolean;
    horizontalPosition: HorizontalPosition;
    verticalPosition: VerticalPosition;
    breakpoints?: KeyVisualBreakpoint[];
    altText?: string;
}

export interface HeroConfig {
    background: BackgroundType;
    solidColor?: string;
    keyVisualSplit: 'full' | 'split-left' | 'split-right';
    title: HeroTitleConfig;
    leadGraphic?: LeadGraphicConfig;
    cta: HeroCTAConfig;
}

export interface KeyVisualBreakpoint {
    minWidth: number;
    url: string;
    scale?: number;
    maxHeight?: number;
    focalPoint?: {
        x: number;
        y: number;
    };
}

export interface KeyVisualConfig {
    isFullWidth: boolean;
    breakpoints?: KeyVisualBreakpoint[];
    altText?: string;
}

export interface MetricsConfig {
    detached?: {
        desktop: boolean;
        mobile: boolean;
    };
    offset?: {
        value: number;
        unit: string;
    };
    gap?: {
        desktop?: CSSValueWithUnit;
        mobile?: CSSValueWithUnit;
    };
    borderRadius?: CSSValueWithUnit;
}

export interface CampaignFlowConfig {
    type: 'interactive' | 'graphic';
    breakpoints?: KeyVisualBreakpoint[];
    altText?: string;
}

export type CountdownVariant = 'solid' | 'border-bottom';
export type CountdownColorTheme = 'primary' | 'secondary';

export interface CountdownConfig {
    isFullWidth: boolean;
    textStyle?: TextStyleConfig;
    padding?: string;
    variant?: CountdownVariant;
    colorTheme?: CountdownColorTheme;
}

export interface NavMenuConfig {
    isFullWidth: boolean;
    logoUrl?: string;
    navElementsStyle?: TextStyleConfig;
    backgroundColor?: string;
    activeElement?: {
        hasUnderline?: boolean;
        colorTheme?: 'default' | 'primary' | 'secondary';
        colorOnlyUnderline?: boolean;
    };
}

export interface FooterConfig {
    isFullWidth: boolean;
    logoUrl?: string;
    footerElementsStyle?: TextStyleConfig;
    backgroundColor?: string;
    padding?: {
        vertical?: string;
        horizontal?: string;
    };
    gap?: {
        desktop?: CSSValueWithUnit;
        mobile?: CSSValueWithUnit;
    };
}

export interface ProjectPageConfig {
    contestantAnswerHeaders: string[];
}

export interface ClientRenderConfig {
    _id?: string;
    hero: HeroConfig;
    countdown?: CountdownConfig;
    navMenu?: NavMenuConfig;
    footer?: FooterConfig;
    keyVisual?: KeyVisualConfig;
    metrics?: MetricsConfig;
    campaignFlow?: CampaignFlowConfig;
    projectPage?: ProjectPageConfig;
}

interface DesignState {
    config: ClientRenderConfig | null;
    loading: boolean;
    error: Error | null;
}

interface DesignActions {
    fetchDesign: () => Promise<void>;
    updateLocalConfig: (updates: Partial<ClientRenderConfig>) => void;
    updateLocalHeroConfig: (updates: Partial<HeroConfig>) => void;
    updateLocalHeroTitleConfig: (updates: Partial<HeroTitleConfig>) => void;
    updateLocalHeroLeadGraphicConfig: (updates: Partial<LeadGraphicConfig>) => void;
    updateLocalHeroCTAConfig: (updates: Partial<HeroCTAConfig>) => void;
    updateLocalKeyVisualConfig: (updates: Partial<KeyVisualConfig>) => void;
    updateLocalMetricsConfig: (updates: Partial<MetricsConfig>) => void;
    updateLocalCampaignFlowConfig: (updates: Partial<CampaignFlowConfig>) => void;
    updateLocalCountdownConfig: (updates: Partial<CountdownConfig>) => void;
    updateLocalNavMenuConfig: (updates: Partial<NavMenuConfig>) => void;
    updateLocalFooterConfig: (updates: Partial<FooterConfig>) => void;
    updateLocalProjectPageConfig: (updates: Partial<ProjectPageConfig>) => void;
}

export type DesignStore = DesignState & DesignActions;

const defaultHeroTitleConfig: HeroTitleConfig = {
    hasTitle: false,
    hasSubTitle: false,
    horizontalPosition: 'left',
    verticalPosition: 'top',
};

const defaultHeroCTAConfig: HeroCTAConfig = {
    hasCTA: false,
    horizontalPosition: 'left',
    verticalPosition: 'top',
    title: {
        text: '',
        style: {},
    },
    body: {
        text: '',
        style: {},
    },
    button: {
        text: '',
        color: '',
        linkUrl: '',
        isOutlined: false,
        isVisible: true,
        style: {},
    },
    card: {
        backgroundColor: '',
        border: '',
        backdropFilter: '',
        boxShadow: '',
        hasNoCard: false,
    },
};

const defaultHeroLeadGraphicConfig: LeadGraphicConfig = {
    hasLeadGraphic: false,
    horizontalPosition: 'left',
    verticalPosition: 'top',
    breakpoints: [],
    altText: '',
};

const defaultHeroConfig: HeroConfig = {
    background: 'keyVisual',
    solidColor: '',
    keyVisualSplit: 'full',
    title: defaultHeroTitleConfig,
    leadGraphic: defaultHeroLeadGraphicConfig,
    cta: defaultHeroCTAConfig,
};

const defaultKeyVisualConfig: KeyVisualConfig = {
    isFullWidth: false,
    breakpoints: [],
    altText: undefined,
};

const defaultMetricsConfig: MetricsConfig = {
    detached: {
        desktop: false,
        mobile: false,
    },
    offset: {
        value: -33.33,
        unit: '%',
    },
    gap: {
        desktop: undefined,
        mobile: undefined,
    },
    borderRadius: undefined,
};

const defaultCampaignFlowConfig: CampaignFlowConfig = {
    type: 'interactive',
    breakpoints: [],
    altText: undefined,
};

const defaultCountdownConfig: CountdownConfig = {
    isFullWidth: false,
    variant: 'solid',
    colorTheme: 'primary',
};

const defaultNavMenuConfig: NavMenuConfig = {
    isFullWidth: false,
    logoUrl: '',
    activeElement: {
        hasUnderline: false,
        colorTheme: 'default',
        colorOnlyUnderline: false,
    },
};

const defaultFooterConfig: FooterConfig = {
    isFullWidth: false,
    logoUrl: '',
    padding: {
        vertical: undefined,
        horizontal: undefined,
    },
    gap: {
        desktop: undefined,
        mobile: undefined,
    },
};

const defaultProjectPageConfig: ProjectPageConfig = {
    contestantAnswerHeaders: [
        'Unsere Antwort auf die erste Frage',
        'Unsere Antwort auf die zweite Frage',
        'Unsere Antwort auf die dritte Frage',
    ],
};

const defaultClientRenderConfig: ClientRenderConfig = {
    hero: defaultHeroConfig,
    countdown: defaultCountdownConfig,
    navMenu: defaultNavMenuConfig,
    footer: defaultFooterConfig,
    keyVisual: defaultKeyVisualConfig,
    metrics: defaultMetricsConfig,
    campaignFlow: defaultCampaignFlowConfig,
    projectPage: defaultProjectPageConfig,
};

export const useDesignStore = create<DesignStore>((set) => ({
    config: null,
    loading: true,
    error: null,

    fetchDesign: async () => {
        try {
            set({ loading: true, error: null });
            const response = await axios.get<ClientRenderConfig[]>('/clientRender');
            const config =
                response.data && response.data.length > 0
                    ? response.data[0]
                    : JSON.parse(JSON.stringify(defaultClientRenderConfig));
            set({ config, loading: false });
        } catch (err) {
            console.error('Failed to fetch design configuration:', err);
            set({
                error: err instanceof Error ? err : new Error(String(err)),
                loading: false,
                // Fallback to default config on error to avoid crashing the app
                config: JSON.parse(JSON.stringify(defaultClientRenderConfig)),
            });
        }
    },

    updateLocalConfig: (updates) => {
        set((state) => {
            const current = state.config ?? defaultClientRenderConfig;
            return {
                config: {
                    ...current,
                    ...updates,
                    hero: updates.hero ? { ...current.hero, ...updates.hero } : current.hero,
                    countdown: updates.countdown ? { ...current.countdown, ...updates.countdown } : current.countdown,
                    navMenu: updates.navMenu ? { ...current.navMenu, ...updates.navMenu } : current.navMenu,
                    footer: updates.footer ? { ...current.footer, ...updates.footer } : current.footer,
                    keyVisual: updates.keyVisual ? { ...current.keyVisual, ...updates.keyVisual } : current.keyVisual,
                    metrics: updates.metrics ? { ...current.metrics, ...updates.metrics } : current.metrics,
                    campaignFlow: updates.campaignFlow
                        ? { ...current.campaignFlow, ...updates.campaignFlow }
                        : current.campaignFlow,
                    projectPage: updates.projectPage
                        ? { ...current.projectPage, ...updates.projectPage }
                        : current.projectPage,
                },
            };
        });
    },

    updateLocalHeroConfig: (updates) => {
        set((state) => {
            const currentHero = state.config?.hero ?? defaultHeroConfig;
            const updatedHero = { ...currentHero, ...updates };
            return {
                config: state.config
                    ? { ...state.config, hero: updatedHero }
                    : { ...defaultClientRenderConfig, hero: updatedHero },
            };
        });
    },

    updateLocalHeroTitleConfig: (updates) => {
        set((state) => {
            const currentHero = state.config?.hero ?? defaultHeroConfig;
            const updatedTitle = { ...currentHero.title, ...updates };
            if (updates.textStyle !== undefined) {
                updatedTitle.textStyle = {
                    ...currentHero.title.textStyle,
                    ...updates.textStyle,
                };
            }
            if (updates.subtitleStyle !== undefined) {
                updatedTitle.subtitleStyle = {
                    ...currentHero.title.subtitleStyle,
                    ...updates.subtitleStyle,
                };
            }
            const updatedHero = { ...currentHero, title: updatedTitle };
            return {
                config: state.config
                    ? { ...state.config, hero: updatedHero }
                    : { ...defaultClientRenderConfig, hero: updatedHero },
            };
        });
    },

    updateLocalHeroLeadGraphicConfig: (updates) => {
        set((state) => {
            const currentHero = state.config?.hero ?? defaultHeroConfig;
            const updatedLeadGraphic = { ...(currentHero.leadGraphic ?? defaultHeroLeadGraphicConfig), ...updates };
            const updatedHero = { ...currentHero, leadGraphic: updatedLeadGraphic };
            return {
                config: state.config
                    ? { ...state.config, hero: updatedHero }
                    : { ...defaultClientRenderConfig, hero: updatedHero },
            };
        });
    },

    updateLocalHeroCTAConfig: (updates) => {
        set((state) => {
            const currentHero = state.config?.hero ?? defaultHeroConfig;
            const updatedCTA = { ...currentHero.cta, ...updates };
            if (updates.title !== undefined) {
                updatedCTA.title = {
                    ...currentHero.cta.title,
                    ...updates.title,
                    style: {
                        ...currentHero.cta.title?.style,
                        ...updates.title.style,
                    },
                };
            }
            if (updates.body !== undefined) {
                updatedCTA.body = {
                    ...currentHero.cta.body,
                    ...updates.body,
                    style: {
                        ...currentHero.cta.body?.style,
                        ...updates.body.style,
                    },
                };
            }
            if (updates.button !== undefined) {
                updatedCTA.button = {
                    ...currentHero.cta.button,
                    ...updates.button,
                    style: {
                        ...currentHero.cta.button?.style,
                        ...updates.button.style,
                    },
                };
            }
            if (updates.card !== undefined) {
                updatedCTA.card = {
                    ...currentHero.cta.card,
                    ...updates.card,
                };
            }
            const updatedHero = { ...currentHero, cta: updatedCTA };
            return {
                config: state.config
                    ? { ...state.config, hero: updatedHero }
                    : { ...defaultClientRenderConfig, hero: updatedHero },
            };
        });
    },

    updateLocalKeyVisualConfig: (updates) => {
        set((state) => {
            const currentKeyVisual = state.config?.keyVisual ?? defaultKeyVisualConfig;
            const updatedKeyVisual = { ...currentKeyVisual, ...updates };
            return {
                config: state.config
                    ? { ...state.config, keyVisual: updatedKeyVisual }
                    : { ...defaultClientRenderConfig, keyVisual: updatedKeyVisual },
            };
        });
    },

    updateLocalMetricsConfig: (updates) => {
        set((state) => {
            const currentMetrics = state.config?.metrics ?? defaultMetricsConfig;
            const updatedMetrics = { ...currentMetrics, ...updates };
            if (updates.detached !== undefined) {
                updatedMetrics.detached = {
                    ...currentMetrics.detached,
                    ...updates.detached,
                };
            }
            if (updates.offset !== undefined) {
                updatedMetrics.offset = {
                    ...currentMetrics.offset,
                    ...updates.offset,
                };
            }
            if (updates.gap !== undefined) {
                updatedMetrics.gap = {
                    ...currentMetrics.gap,
                    ...updates.gap,
                };
            }
            return {
                config: state.config
                    ? { ...state.config, metrics: updatedMetrics }
                    : { ...defaultClientRenderConfig, metrics: updatedMetrics },
            };
        });
    },

    updateLocalCampaignFlowConfig: (updates) => {
        set((state) => {
            const currentCampaignFlow = state.config?.campaignFlow ?? defaultCampaignFlowConfig;
            const updatedCampaignFlow = { ...currentCampaignFlow, ...updates };
            return {
                config: state.config
                    ? { ...state.config, campaignFlow: updatedCampaignFlow }
                    : { ...defaultClientRenderConfig, campaignFlow: updatedCampaignFlow },
            };
        });
    },

    updateLocalCountdownConfig: (updates) => {
        set((state) => {
            const currentCountdown = state.config?.countdown ?? defaultCountdownConfig;
            const updatedCountdown = { ...currentCountdown, ...updates };
            if (updates.textStyle !== undefined) {
                updatedCountdown.textStyle = {
                    ...currentCountdown.textStyle,
                    ...updates.textStyle,
                };
            }
            return {
                config: state.config
                    ? { ...state.config, countdown: updatedCountdown }
                    : { ...defaultClientRenderConfig, countdown: updatedCountdown },
            };
        });
    },

    updateLocalNavMenuConfig: (updates) => {
        set((state) => {
            const currentNavMenu = state.config?.navMenu ?? defaultNavMenuConfig;
            const updatedNavMenu = { ...currentNavMenu, ...updates };
            if (updates.activeElement !== undefined) {
                updatedNavMenu.activeElement = {
                    ...currentNavMenu.activeElement,
                    ...updates.activeElement,
                };
            }
            if (updates.navElementsStyle !== undefined) {
                updatedNavMenu.navElementsStyle = {
                    ...currentNavMenu.navElementsStyle,
                    ...updates.navElementsStyle,
                };
            }
            return {
                config: state.config
                    ? { ...state.config, navMenu: updatedNavMenu }
                    : { ...defaultClientRenderConfig, navMenu: updatedNavMenu },
            };
        });
    },

    updateLocalFooterConfig: (updates) => {
        set((state) => {
            const currentFooter = state.config?.footer ?? defaultFooterConfig;
            const updatedFooter = { ...currentFooter, ...updates };
            if (updates.padding !== undefined) {
                updatedFooter.padding = {
                    ...currentFooter.padding,
                    ...updates.padding,
                };
            }
            if (updates.gap !== undefined) {
                updatedFooter.gap = {
                    ...currentFooter.gap,
                    ...updates.gap,
                };
            }
            if (updates.footerElementsStyle !== undefined) {
                updatedFooter.footerElementsStyle = {
                    ...currentFooter.footerElementsStyle,
                    ...updates.footerElementsStyle,
                };
            }
            return {
                config: state.config
                    ? { ...state.config, footer: updatedFooter }
                    : { ...defaultClientRenderConfig, footer: updatedFooter },
            };
        });
    },

    updateLocalProjectPageConfig: (updates) => {
        set((state) => {
            const currentProjectPage = state.config?.projectPage ?? defaultProjectPageConfig;
            const updatedProjectPage = { ...currentProjectPage, ...updates };
            return {
                config: state.config
                    ? { ...state.config, projectPage: updatedProjectPage }
                    : { ...defaultClientRenderConfig, projectPage: updatedProjectPage },
            };
        });
    },
}));

/**
 * Helper to safely get the design config and fall back to defaults if not yet loaded or missing.
 */
const useSafeDesignConfig = (): ClientRenderConfig => {
    const config = useDesignStore((state) => state.config);
    return config ?? defaultClientRenderConfig;
};

// Strongly-typed hooks to expose specific design configuration sections
export const useHeroConfig = (): HeroConfig => {
    return useSafeDesignConfig().hero ?? defaultHeroConfig;
};

export const useKeyVisualConfig = (): KeyVisualConfig => {
    return useSafeDesignConfig().keyVisual ?? defaultKeyVisualConfig;
};

export const useCountdownConfig = (): CountdownConfig => {
    return useSafeDesignConfig().countdown ?? defaultCountdownConfig;
};

export const useNavMenuConfig = (): NavMenuConfig => {
    return useSafeDesignConfig().navMenu ?? defaultNavMenuConfig;
};

export const useFooterConfig = (): FooterConfig => {
    return useSafeDesignConfig().footer ?? defaultFooterConfig;
};

export const useCampaignFlowConfig = (): CampaignFlowConfig => {
    return useSafeDesignConfig().campaignFlow ?? defaultCampaignFlowConfig;
};

export const useProjectPageConfig = (): ProjectPageConfig => {
    return useSafeDesignConfig().projectPage ?? defaultProjectPageConfig;
};

export interface ResolvedMetricsConfig extends MetricsConfig {
    detached: {
        desktop: boolean;
        mobile: boolean;
    };
    offset: {
        value: number;
        unit: string;
    };
}

export const useMetricsConfig = (): ResolvedMetricsConfig => {
    const metrics = useSafeDesignConfig().metrics ?? defaultMetricsConfig;
    const offsetUnit = metrics.offset?.unit ?? '%';
    let offsetValue = metrics.offset?.value;
    if (offsetValue === undefined) {
        if (offsetUnit === '%') offsetValue = -33.33;
        else if (offsetUnit === 'px') offsetValue = -30;
        else offsetValue = -2;
    }
    return {
        ...metrics,
        detached: {
            desktop: metrics.detached?.desktop ?? false,
            mobile: metrics.detached?.mobile ?? false,
        },
        offset: {
            value: offsetValue,
            unit: offsetUnit,
        },
    };
};
